import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3, Calendar, Users, FileText, Clock,
  TrendingUp, Play, Filter, Search
} from 'lucide-react';
import '../styles/personaInterview.css';

function PersonaSurveyHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent, oldest, most_responses

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://n8n.iniai.cloud/webhook/get-completed-surveys', {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      console.log('Surveys fetched:', data);

      // Filter out surveys with null survey_name and clean data
      const cleanedSurveys = data
        .filter(survey => survey.survey_name !== null)
        .map(survey => ({
          ...survey,
          survey_name: survey.survey_name || survey.survey_id
        }));

      setSurveys(cleanedSurveys);
    } catch (error) {
      console.error('Error fetching surveys:', error);

      
    } finally {
      setLoading(false);
    }
  };

  const filteredSurveys = surveys
    .filter(survey =>
      survey.survey_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch(sortBy) {
        case 'recent':
          return new Date(b.survey_completed) - new Date(a.survey_completed);
        case 'oldest':
          return new Date(a.survey_completed) - new Date(b.survey_completed);
        case 'most_responses':
          return parseInt(b.total_responses) - parseInt(a.total_responses);
        default:
          return 0;
      }
    });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const mins = parseFloat(minutes);
    if (mins < 60) return `${mins.toFixed(0)} min`;
    const hours = Math.floor(mins / 60);
    const remainingMins = Math.round(mins % 60);
    return `${hours}h ${remainingMins}m`;
  };

  return (
    <div className="persona-interview-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <BarChart3 size={24} />
            <h1>Survey History</h1>
          </div>
          <p>View and analyze completed persona surveys</p>
        </div>
        <button
          onClick={() => navigate('/persona-survey-quant')}
          className="run-survey-btn"
        >
          <Play size={16} />
          Run New Survey
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <Filter size={20} />
          <h3>Filter & Sort</h3>
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label>Search Surveys</label>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by survey name..."
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </div>

          <div className="filter-group">
            <label>Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Most Recent</option>
              <option value="oldest">Oldest First</option>
              <option value="most_responses">Most Responses</option>
            </select>
          </div>
        </div>
      </div>

      {/* Survey List */}
      <div className="filters-section">
        <div className="filters-header">
          <BarChart3 size={20} />
          <h3>Completed Surveys ({filteredSurveys.length})</h3>
        </div>

        {loading && (
          <div className="surveys-loading">
            <div className="loading-spinner"></div>
            <p>Loading surveys...</p>
          </div>
        )}

        {!loading && filteredSurveys.length === 0 && (
          <div className="no-surveys">
            <BarChart3 size={48} />
            <h3>No surveys found</h3>
            <p>Run your first survey to get started</p>
            <button
              onClick={() => navigate('/persona-survey-quant')}
              className="run-survey-btn"
              style={{ marginTop: '16px' }}
            >
              <Play size={16} />
              Run New Survey
            </button>
          </div>
        )}

        {!loading && filteredSurveys.length > 0 && (
          <div className="survey-history-grid">
            {filteredSurveys.map((survey, index) => (
              <div
                key={index}
                className="survey-history-card"
                onClick={() => navigate(`/survey-analysis/${survey.survey_id}`)}
              >
                <div className="survey-card-header">
                  <h3>{survey.survey_name}</h3>
                  <span className={`status-badge ${survey.completion_status.toLowerCase()}`}>
                    {survey.completion_status}
                  </span>
                </div>

                <div className="survey-card-stats">
                  <div className="stat-item">
                    <Calendar size={16} />
                    <span>{formatDate(survey.survey_completed)}</span>
                  </div>
                  <div className="stat-item">
                    <Clock size={16} />
                    <span>{formatDuration(survey.duration_minutes)}</span>
                  </div>
                </div>

                <div className="survey-card-metrics">
                  <div className="metric">
                    <Users size={20} />
                    <div>
                      <span className="metric-value">{survey.personas_count}</span>
                      <span className="metric-label">Personas</span>
                    </div>
                  </div>
                  <div className="metric">
                    <FileText size={20} />
                    <div>
                      <span className="metric-value">{survey.questions_count}</span>
                      <span className="metric-label">Questions</span>
                    </div>
                  </div>
                  <div className="metric">
                    <BarChart3 size={20} />
                    <div>
                      <span className="metric-value">{survey.total_responses}</span>
                      <span className="metric-label">Responses</span>
                    </div>
                  </div>
                  <div className="metric">
                    <TrendingUp size={20} />
                    <div>
                      <span className="metric-value">{survey.avg_scale_rating}</span>
                      <span className="metric-label">Avg Rating</span>
                    </div>
                  </div>
                </div>

                <div className="survey-card-breakdown">
                  <span className="breakdown-item">
                    <strong>{survey.scale_responses}</strong> scale
                  </span>
                  <span className="breakdown-item">
                    <strong>{survey.text_responses}</strong> text
                  </span>
                  <span className="breakdown-item">
                    <strong>{survey.skipped_responses}</strong> skipped
                  </span>
                  <span className="breakdown-item">
                    <strong>{survey.avg_response_time_seconds}s</strong> avg time
                  </span>
                </div>

                <div className="survey-card-actions">
                  <button className="view-results-btn">
                    View Results →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonaSurveyHistory;